import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import TaskDetailsTab from './TaskDetailsTab';
import LayoutThemesTab from './LayoutThemesTab';

const Settings = ({ open, onClose }) => {
  const [showCompletedTasks, setShowCompletedTasks] = useState(true);
  const [layoutMode, setLayoutMode] = useState('side-by-side');
  const [showDescription, setShowDescription] = useState(true);
  const [moveCompletedToBottom, setMoveCompletedToBottom] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [showTaskNumbers, setShowTaskNumbers] = useState(false);
  const [theme, setTheme] = useState('prime');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [soundType, setSoundType] = useState('pencil');

  useEffect(() => {
    const savedShowCompleted = localStorage.getItem('showCompletedTasks');
    if (savedShowCompleted !== null) setShowCompletedTasks(savedShowCompleted === 'true');

    const savedLayout = localStorage.getItem('layoutMode');
    if (savedLayout) setLayoutMode(savedLayout);

    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode !== null) {
      setDarkMode(savedDarkMode === 'true');
      if (savedDarkMode === 'true') document.documentElement.classList.add('dark');
    }

    const savedShowDescription = localStorage.getItem('showDescription');
    if (savedShowDescription !== null) setShowDescription(savedShowDescription === 'true');

    const savedMoveCompleted = localStorage.getItem('moveCompletedToBottom');
    if (savedMoveCompleted !== null) setMoveCompletedToBottom(savedMoveCompleted === 'true');

    const savedShowTaskNumbers = localStorage.getItem('showTaskNumbers');
    if (savedShowTaskNumbers !== null) setShowTaskNumbers(savedShowTaskNumbers === 'true');

    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) setTheme(savedTheme);
  }, [open]);

  const dispatchSetting = (event, detail) => {
    window.dispatchEvent(new CustomEvent(event, { detail }));
  };

  const updateSetting = (key, value, setter, eventName, eventKey) => {
    setter(value);
    localStorage.setItem(key, typeof value === 'boolean' ? value.toString() : value);
    dispatchSetting(eventName, { [eventKey]: value });
  };

  const handleDarkModeToggle = (checked) => {
    setDarkMode(checked);
    localStorage.setItem('darkMode', checked.toString());
    document.documentElement.classList.toggle('dark', checked);
  };

  const handleResetDefaults = () => {
    const isMobile = window.innerWidth < 768;
    const defaultLayout = isMobile ? 'priority-stacked' : 'side-by-side';

    localStorage.setItem('showCompletedTasks', 'true');
    localStorage.setItem('moveCompletedToBottom', 'true');
    localStorage.setItem('showDescription', 'true');
    localStorage.setItem('showTaskNumbers', 'false');
    localStorage.setItem('layoutMode', defaultLayout);
    localStorage.setItem('theme', 'prime');
    localStorage.setItem('darkMode', 'true');
    localStorage.setItem('soundEnabled', 'true');
    localStorage.setItem('soundType', 'pencil');

    setShowCompletedTasks(true);
    setMoveCompletedToBottom(true);
    setShowDescription(true);
    setShowTaskNumbers(false);
    setLayoutMode(defaultLayout);
    setTheme('prime');
    setDarkMode(true);
    setSoundEnabled(true);
    setSoundType('pencil');
    document.documentElement.classList.add('dark');

    dispatchSetting('settingsChanged', { showCompletedTasks: true });
    dispatchSetting('layoutChanged', { layoutMode: defaultLayout });
    dispatchSetting('descriptionVisibilityChanged', { showDescription: true });
    dispatchSetting('moveCompletedSettingChanged', { moveCompletedToBottom: true });
    dispatchSetting('taskNumbersVisibilityChanged', { showTaskNumbers: false });
    dispatchSetting('themeChanged', { theme: 'prime' });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Settings</DialogTitle>
          <DialogDescription>Customize your task management experience</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="task-details" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="task-details" data-testid="tab-task-details">Task Details</TabsTrigger>
            <TabsTrigger value="layout-themes" data-testid="tab-layout-themes">Layout / Themes</TabsTrigger>
          </TabsList>

          <TabsContent value="task-details">
            <TaskDetailsTab
              showCompletedTasks={showCompletedTasks}
              onShowCompletedChange={(v) => updateSetting('showCompletedTasks', v, setShowCompletedTasks, 'settingsChanged', 'showCompletedTasks')}
              moveCompletedToBottom={moveCompletedToBottom}
              onMoveCompletedChange={(v) => updateSetting('moveCompletedToBottom', v, setMoveCompletedToBottom, 'moveCompletedSettingChanged', 'moveCompletedToBottom')}
              showDescription={showDescription}
              onShowDescriptionChange={(v) => updateSetting('showDescription', v, setShowDescription, 'descriptionVisibilityChanged', 'showDescription')}
              showTaskNumbers={showTaskNumbers}
              onShowTaskNumbersChange={(v) => updateSetting('showTaskNumbers', v, setShowTaskNumbers, 'taskNumbersVisibilityChanged', 'showTaskNumbers')}
              soundEnabled={soundEnabled}
              onSoundEnabledChange={(v) => { setSoundEnabled(v); localStorage.setItem('soundEnabled', v.toString()); }}
              soundType={soundType}
              onSoundTypeChange={(v) => { setSoundType(v); localStorage.setItem('soundType', v); }}
              onResetDefaults={handleResetDefaults}
            />
          </TabsContent>

          <TabsContent value="layout-themes">
            <LayoutThemesTab
              layoutMode={layoutMode}
              onLayoutChange={(v) => updateSetting('layoutMode', v, setLayoutMode, 'layoutChanged', 'layoutMode')}
              theme={theme}
              onThemeChange={(v) => updateSetting('theme', v, setTheme, 'themeChanged', 'theme')}
              darkMode={darkMode}
              onDarkModeToggle={handleDarkModeToggle}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default Settings;
